/**
 * A Workshop thread in the M7 rail's language (stage 2a).
 *
 * The rail's own parts do the drawing, unchanged: one time per cluster, your
 * words in a plane, every agent turn opening on the M7 mark, "Thought for Xs",
 * tools as quiet rows (a failed one reads as failed), replies with accent marks,
 * the model that answered under each reply, and the question card in pick A.
 * This file only decides what goes where, and adds what a Workshop thread has
 * and a side chat does not: output cards, shared threads (who wrote what, the
 * assistant's seen mark, cards anyone may answer), and the compaction divider.
 */
import {
  Fragment,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from "react";
import { BadgeCheck, Loader2, MessageCircleQuestion } from "lucide-react";
import type {
  ChatMessage,
  ChatModel,
  ChatToolEvent,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import type { StreamingBlock } from "@/app/routes/dashboard/components/streamingTurnBlocks";
import { splitTurnRounds } from "@/app/routes/dashboard/components/turnRounds";
import { getMessageThinking } from "@/app/routes/dashboard/components/ThoughtSummary";
import { getMessageUsage } from "@/app/routes/dashboard/components/MessageUsageBadge";
import { isCustomRenderedTool, type RailToolItem } from "@/app/routes/dashboard/components/railTools";
import { attachedVideosOf } from "@/app/routes/dashboard/components/attachedVideos";
import { parseAskUserQuestions } from "@/app/routes/dashboard/components/askUserQuestion";
import { parseVerifyRequest } from "@/app/routes/dashboard/components/verifyWithUser";
import {
  findCardResponses,
  findOpenSharedCard,
  findPendingInteraction,
} from "@/app/routes/dashboard/components/agentInteractions";
import {
  ClusterTime,
  HumanBubble,
  MarkRow,
  Notice,
  RailMark,
  ReplyFoot,
  ReplyText,
  StreamError,
  StreamingText,
  ThinkingRow,
  ToolRows,
  useTapToReveal,
} from "../../agent/rail/RailParts";
import { AgentVideos, LinkEmbeds, SentFiles } from "../../agent/rail/RailMedia";
import { standaloneLinks } from "../../agent/rail/railLinks";
import {
  QuestionAnswered,
  RailQuestionCard,
  RailVerifyCard,
  VerifyAnswered,
} from "../../agent/rail/RailCards";
import {
  answeringModel,
  messageTime,
  startsCluster,
  type AttachmentClaims,
} from "../../agent/rail/railFormat";
import "../../agent/rail/rail.css";
import { useSendFlight, type SendFlight } from "../../agent/rail/sendFlight";
import { motionReduced } from "@/lib/motion";
import {
  cardsOf,
  placeThreadFiles,
  readThreadCardAnswers,
  workshopPersistedTool,
  workshopStreamingTool,
  type CardOutcome,
} from "../workshopThread";
import { AssistantSeenBadge } from "./SharedThreadStatus";
import { QuickCreatePills } from "./QuickCreateParts";
import { messageQuickCreates } from "../quickCreate";

export type WorkshopSharedView = {
  viewerUserId: string | null;
  sharedAt: string | null;
  /** The newest message the assistant has read: it carries the seen mark. */
  lastSeenMessageId: string | null;
};

type Props = {
  messages: ChatMessage[];
  /** Every file in the thread, sent or waiting; each is placed by its message. */
  attachments: ChatAttachmentItem[];
  /** Videos the agent attached from links, by document id. */
  linkedAttachments: Map<string, ChatAttachmentItem>;
  /** Files sent from this tab, by the message they went out with. */
  attachmentClaims: AttachmentClaims;
  isLoading: boolean;
  isStreaming: boolean;
  streamingBlocks: StreamingBlock[];
  /** Shared threads: where the live reply sits (people keep writing meanwhile). */
  streamingAt: string | null;
  streamError: string | null;
  /** The picker's list, to name the model that answered. */
  models: ChatModel[];
  emptyState: ReactNode;
  /** Null for a private thread. */
  shared: WorkshopSharedView | null;
  /** The last message the compacted summary covers: the divider sits under it. */
  compactedAtMessageId: string | null;
  /** Sends a card's answer. Absent, every card renders read-only. */
  onRespond?: (reply: string, cardMessageId: string) => void;
  respondDisabled: boolean;
  onRetryAttachment?: (documentId: string) => void;
  /** Output cards for a round's finished tool calls (documents, ideas, pictures). */
  renderOutputs: (events: (ChatToolEvent | StreamingToolEvent)[]) => ReactNode;
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Where the words of your last send left the composer (they fly into the bubble). */
  flightRef?: MutableRefObject<SendFlight | null>;
  /**
   * The thread is read a page at a time: older messages exist above the first
   * one here (their row holds the place), and a page of them may be loading.
   */
  olderPages?: { hasOlder: boolean; loading: boolean };
  /** Private: your messages in the pages not loaded (file claims count them). */
  claimOffset?: number;
  ariaLabel: string;
};

/**
 * The top of a thread read a page at a time. It holds its place while older
 * messages exist, so starting to load them never pushes the thread down; it
 * spins while they load.
 */
function OlderMessagesRow({ loading }: { loading: boolean }) {
  return (
    <div className="ws-older" role="status" aria-live="polite">
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden strokeWidth={2} />
          <span className="co-sr-only">Loading earlier messages</span>
        </>
      ) : null}
    </div>
  );
}

type Entry = {
  key: string;
  createdAt: string;
  /** A divider stands on its own: no cluster time before it. */
  divider?: boolean;
  node: ReactNode;
};

const ANYONE_CAN_ANSWER = "Anyone in this chat can answer. The first answer goes to the agent.";

/** "You" or the person's name. */
function authorLabel(message: Pick<ChatMessage, "authorUserId" | "authorName">, viewer: string | null) {
  return !message.authorUserId || message.authorUserId === viewer
    ? "You"
    : message.authorName?.trim() || "A teammate";
}

/** Shared threads: the name shows when the speaker changes, like a group chat. */
function namedMessageIds(messages: ChatMessage[], viewer: string | null): Set<string> {
  const ids = new Set<string>();
  let previous: string | null = null;
  for (const message of messages) {
    if (message.role !== "user") {
      previous = null;
      continue;
    }
    const author = message.authorUserId || viewer || "";
    if (author !== previous) ids.add(message.id);
    previous = author;
  }
  return ids;
}

function AuthorLine({ label, at }: { label: string; at: string }) {
  return (
    <p className="ws-author" data-self={label === "You" || undefined}>
      {label}
      <span aria-hidden="true"> · </span>
      <time dateTime={at}>{messageTime(at)}</time>
    </p>
  );
}

/** Lands with a little give: overshoots its stop by a hair, then settles. */
const LAND_EASE = "cubic-bezier(0.34, 1.4, 0.64, 1)";

/**
 * The assistant's seen mark moves to the newest message it read: it lifts off
 * the bubble it sat on, glides along the thread and lands on the new one with
 * a small pop. The first mark in a thread pops in where it lands.
 *
 * Positions are kept in the log's scroll coordinates and read from layout
 * (offsets, not the painted box), so scrolling between two reads, or a glide
 * still running, is never mistaken for where the mark sits. A thread opening
 * (loading) shows its mark as it is, without a move.
 */
function useSeenMarkGlide(
  scrollRef: RefObject<HTMLDivElement | null>,
  seenId: string | null,
  isLoading: boolean,
) {
  const last = useRef<{ id: string; x: number; y: number } | null>(null);
  const primed = useRef(false);
  useLayoutEffect(() => {
    if (isLoading) {
      last.current = null;
      primed.current = false;
      return;
    }
    const log = scrollRef.current;
    const mark = seenId ? log?.querySelector<HTMLElement>(".ws-seen-badge") : null;
    const parent = mark?.offsetParent as HTMLElement | null | undefined;
    if (!log || !mark || !parent) {
      // Nothing read yet: whatever lands first, pops in.
      if (!seenId) primed.current = true;
      return;
    }
    const box = log.getBoundingClientRect();
    const host = parent.getBoundingClientRect();
    const here = {
      id: seenId!,
      x: host.left - box.left + log.scrollLeft + mark.offsetLeft,
      y: host.top - box.top + log.scrollTop + mark.offsetTop,
    };
    const before = last.current;
    last.current = here;
    const firstLook = !primed.current;
    primed.current = true;
    if (firstLook || before?.id === seenId || motionReduced()) return;
    if (!before) {
      mark.animate(
        [
          { transform: "scale(0.3)", opacity: 0 },
          { transform: "scale(1)", opacity: 1 },
        ],
        { duration: 360, easing: LAND_EASE },
      );
      return;
    }
    const dx = before.x - here.x;
    const dy = before.y - here.y;
    mark.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(1)` },
        { transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 8}px) scale(1.18)`, offset: 0.55 },
        { transform: "translate(0, 0) scale(1)" },
      ],
      { duration: 560, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  });
}

/** Older than this when it reaches the tab, a message just appears (a reload, an older page). */
const RISE_WINDOW_MS = 30_000;

/**
 * Someone else's message rises into a shared thread, the way it would on a
 * phone; yours flies up from the composer instead (useSendFlight). Only rows
 * that arrive while the thread is open, and recently written, so opening a
 * thread or catching up on a reload never animates.
 */
function useArrivalRise(
  scrollRef: RefObject<HTMLDivElement | null>,
  messages: ChatMessage[],
  viewer: string | null,
  shared: boolean,
  isLoading: boolean,
) {
  const known = useRef<Set<string> | null>(null);
  useLayoutEffect(() => {
    if (isLoading || !shared) {
      known.current = null;
      return;
    }
    const before = known.current;
    known.current = new Set(messages.map((m) => m.id));
    const log = scrollRef.current;
    if (!before || !log || motionReduced()) return;
    const now = Date.now();
    for (const m of messages) {
      if (before.has(m.id) || m.role !== "user") continue;
      if (!m.authorUserId || m.authorUserId === viewer) continue;
      if (now - Date.parse(m.createdAt) > RISE_WINDOW_MS) continue;
      const row = log
        .querySelector<HTMLElement>(`[data-msg-id="${CSS.escape(m.id)}"]`)
        ?.closest<HTMLElement>(".m7r-you");
      if (!row) continue;
      const author = row.previousElementSibling;
      const parts = author?.classList.contains("ws-author") ? [author, row] : [row];
      parts.forEach((el) =>
        el.animate(
          [
            { opacity: 0, transform: "translateY(12px) scale(0.98)" },
            { opacity: 1, transform: "translateY(0) scale(1)" },
          ],
          { duration: 420, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
        ),
      );
    }
  }, [messages, isLoading, shared, viewer, scrollRef]);
}

/** Between tool calls, and before the first frame: the agent is working. */
function WorkingRow({ withMark }: { withMark: boolean }) {
  return (
    <div className="m7r-mark-row">
      {withMark ? <RailMark /> : null}
      <span className="m7r-think m7r-shimmer" role="status">
        Working
      </span>
      <Loader2 className="ws-working-spin animate-spin" aria-hidden strokeWidth={2} />
    </div>
  );
}

/** A card the agent put up, live or folded to what was picked. */
function TurnCard({
  msg,
  live,
  outcome,
  onRespond,
  respondDisabled,
  footnote,
}: {
  msg: ChatMessage;
  live: boolean;
  outcome: CardOutcome | undefined;
  onRespond?: (reply: string, cardMessageId: string) => void;
  respondDisabled: boolean;
  footnote: string | null;
}) {
  const { questions, verify } = cardsOf(msg.toolEvents ?? []);
  if (questions.length === 0 && !verify) return null;
  const submit = live && onRespond ? (reply: string) => onRespond(reply, msg.id) : undefined;
  return (
    <>
      {questions.length > 0 ? (
        submit ? (
          <RailQuestionCard questions={questions} onSubmit={submit} disabled={respondDisabled} />
        ) : (
          <QuestionAnswered questions={questions} answer={outcome?.questions ?? null} />
        )
      ) : verify ? (
        submit ? (
          <RailVerifyCard request={verify} onSubmit={submit} disabled={respondDisabled} />
        ) : (
          <VerifyAnswered request={verify} answer={outcome?.verify ?? null} />
        )
      ) : null}
      {footnote ? <p className="ws-card-by">{footnote}</p> : null}
    </>
  );
}

function AssistantTurn({
  msg,
  models,
  linked,
  card,
  renderOutputs,
  inShared,
}: {
  msg: ChatMessage;
  models: ChatModel[];
  linked: Map<string, ChatAttachmentItem>;
  card: ReactNode;
  renderOutputs: Props["renderOutputs"];
  /** Anyone in a shared thread may stop its reply, so it never says who did. */
  inShared: boolean;
}) {
  const tap = useTapToReveal();
  const thinking = getMessageThinking(msg.metadata);
  const rounds = splitTurnRounds(msg.content, thinking, msg.toolEvents ?? []);
  const hasText = rounds.some((round) => Boolean(round.text));
  const usage = getMessageUsage(msg.metadata);

  return (
    <div className="m7r-turn" data-show={tap.shown || undefined} onClick={tap.onClick}>
      {rounds[0]?.thinking ? null : <MarkRow />}
      {rounds.map((round, r) => {
        const tools: RailToolItem[] = round.tools
          .filter((event) => !isCustomRenderedTool(event.toolName))
          .map(workshopPersistedTool);
        const videos = round.tools.flatMap((event) => attachedVideosOf(event));
        // A link the agent put on its own line shows as its embed.
        const { text, links } = round.text ? standaloneLinks(round.text) : { text: "", links: [] };
        return (
          <Fragment key={`${msg.id}-${r}`}>
            {round.thinking ? <ThinkingRow thinking={round.thinking} withMark={r === 0} /> : null}
            {text ? <ReplyText content={text} /> : null}
            <LinkEmbeds links={links} side="agent" />
            <ToolRows tools={tools} />
            <AgentVideos videos={videos} linked={linked} />
            {renderOutputs(round.tools)}
          </Fragment>
        );
      })}
      {hasText ? (
        <ReplyFoot
          text={msg.content}
          model={answeringModel(msg.metadata, models)}
          modelId={usage?.model ?? null}
          createdAt={msg.createdAt}
          usage={usage}
        />
      ) : null}
      {card}
      {msg.status === "aborted" ? (
        <p className="m7r-stopped">{inShared ? "Stopped" : "Stopped by you"}</p>
      ) : null}
    </div>
  );
}

function streamWait(id: string, title: string, icon: ReactNode) {
  return (
    <p key={id} className="m7r-ask-wait">
      <span className="m7r-ask-wait-icon" aria-hidden>
        {icon}
      </span>
      <span className="m7r-ask-wait-label">{title}</span>
    </p>
  );
}

function StreamingTurn({
  blocks,
  renderOutputs,
}: {
  blocks: StreamingBlock[];
  renderOutputs: Props["renderOutputs"];
}) {
  const nodes: ReactNode[] = [];
  let tools: RailToolItem[] = [];
  let events: StreamingToolEvent[] = [];
  const flushTools = () => {
    if (tools.length === 0 && events.length === 0) return;
    const key = nodes.length;
    nodes.push(<ToolRows key={`tools-${key}`} tools={tools} />);
    nodes.push(<Fragment key={`outputs-${key}`}>{renderOutputs(events)}</Fragment>);
    tools = [];
    events = [];
  };

  blocks.forEach((block, i) => {
    if (block.kind === "thinking") {
      flushTools();
      nodes.push(
        <ThinkingRow
          key={block.block.id}
          thinking={{ text: block.block.text, durationMs: block.block.durationMs }}
          withMark={i === 0}
          live={block.block.isLive}
        />,
      );
    } else if (block.kind === "tool") {
      // The full cards wait until the reply is saved. Until then, a title line,
      // so neither looks like something you can answer yet.
      const asked = parseAskUserQuestions(block.event);
      if (asked) {
        flushTools();
        const title = asked.map((q) => q.header).filter(Boolean).join(" · ") || "Question";
        nodes.push(
          streamWait(
            block.event.toolCallId,
            title,
            <MessageCircleQuestion strokeWidth={1.75} />,
          ),
        );
        return;
      }
      const request = parseVerifyRequest(block.event);
      if (request) {
        flushTools();
        const title = request.items.map((item) => item.label).filter(Boolean).join(" · ") || "Check these values";
        nodes.push(
          streamWait(
            block.event.toolCallId,
            title,
            <BadgeCheck strokeWidth={1.75} />,
          ),
        );
        return;
      }
      if (isCustomRenderedTool(block.event.toolName)) return;
      tools.push(workshopStreamingTool(block.event));
      events.push(block.event);
    } else if (block.block.content.trim()) {
      flushTools();
      nodes.push(
        <StreamingText key={block.block.id} content={block.block.content} live={block.block.isLive} />,
      );
    }
  });
  flushTools();

  const opensOnThinking = blocks[0]?.kind === "thinking";

  // "Working" stays under the live turn for as long as it is live, whatever
  // the agent is doing (thinking, waiting on its model, a tool, writing), so it
  // never blinks off between the steps that show.
  return (
    <div className="m7r-turn" data-live>
      {opensOnThinking ? null : blocks.length === 0 ? <WorkingRow withMark /> : <MarkRow />}
      {nodes}
      {blocks.length > 0 ? <WorkingRow withMark={false} /> : null}
    </div>
  );
}

export default function WorkshopConversation({
  messages,
  attachments,
  linkedAttachments,
  attachmentClaims,
  isLoading,
  isStreaming,
  streamingBlocks,
  streamingAt,
  streamError,
  models,
  emptyState,
  shared,
  compactedAtMessageId,
  onRespond,
  respondDisabled,
  onRetryAttachment,
  renderOutputs,
  scrollRef,
  flightRef,
  olderPages,
  claimOffset = 0,
  ariaLabel,
}: Props) {
  const viewer = shared?.viewerUserId ?? null;
  useSendFlight({
    flightRef,
    scrollRef,
    messages,
    // A shared thread: only your own words fly, the placeholder included (it
    // carries your id before the server names it).
    isOwn: (m) => m.role === "user" && (!shared || !m.authorUserId || m.authorUserId === viewer),
  });
  useSeenMarkGlide(scrollRef, shared?.lastSeenMessageId ?? null, isLoading);
  useArrivalRise(scrollRef, messages, viewer, Boolean(shared), isLoading);
  // Private: only the newest turn's card is open. Shared: a card stays open
  // until someone answers it, even while people talk it over.
  const pendingId = shared
    ? (findOpenSharedCard(messages, findCardResponses(messages), shared.sharedAt)?.messageId ?? null)
    : isStreaming
      ? null
      : (findPendingInteraction(messages)?.messageId ?? null);
  const answers = readThreadCardAnswers(messages, pendingId, shared);
  const files = placeThreadFiles(
    messages,
    attachments,
    attachmentClaims,
    viewer,
    Boolean(shared),
    claimOffset,
  );
  const named = shared ? namedMessageIds(messages, viewer) : null;

  const footnoteFor = (msg: ChatMessage): string | null => {
    if (!shared) return null;
    if (msg.id === pendingId) return ANYONE_CAN_ANSWER;
    const by = answers.byCard.get(msg.id)?.answeredBy;
    return by ? `Answered by ${by === "You" ? "you" : by}` : null;
  };

  const entries: Entry[] = [];
  for (const msg of messages) {
    if (msg.role === "user") {
      const sent = files.byMessage.get(msg.id) ?? [];
      if (answers.echoes.has(msg.id)) {
        // The card shows the answer; files sent with it still need a place.
        if (sent.length > 0) {
          entries.push({
            key: `files-${msg.id}`,
            createdAt: msg.createdAt,
            node: (
              <div className="m7r-you">
                <SentFiles items={sent} onRetry={onRetryAttachment} />
              </div>
            ),
          });
        }
        continue;
      }
      entries.push({
        // A send keeps its placeholder's key once stored: the same bubble, not a new one.
        key: msg.clientKey ?? msg.id,
        createdAt: msg.createdAt,
        node: (
          <>
            {named?.has(msg.id) ? (
              <AuthorLine label={authorLabel(msg, viewer)} at={msg.createdAt} />
            ) : null}
            <HumanBubble
              id={msg.id}
              text={msg.content}
              createdAt={msg.createdAt}
              files={sent}
              onRetryFile={onRetryAttachment}
              lead={sentPill(msg.metadata)}
              corner={shared?.lastSeenMessageId === msg.id ? <AssistantSeenBadge /> : null}
            />
          </>
        ),
      });
    } else {
      entries.push({
        // A reply shown before the server's row came in keeps its key: not mounted again.
        key: msg.clientKey ?? msg.id,
        createdAt: msg.createdAt,
        node: (
          <AssistantTurn
            msg={msg}
            models={models}
            linked={linkedAttachments}
            renderOutputs={renderOutputs}
            inShared={Boolean(shared)}
            card={
              <TurnCard
                msg={msg}
                live={msg.id === pendingId}
                outcome={answers.byCard.get(msg.id)}
                onRespond={onRespond}
                respondDisabled={respondDisabled}
                footnote={footnoteFor(msg)}
              />
            }
          />
        ),
      });
    }
    if (compactedAtMessageId && msg.id === compactedAtMessageId) {
      entries.push({
        key: `compacted-${msg.id}`,
        createdAt: msg.createdAt,
        divider: true,
        node: <Notice>Compacted here. Workshop now reads the messages above as a summary.</Notice>,
      });
    }
  }
  // Someone else's files, not followed by a message of theirs yet.
  for (const item of files.loose) {
    entries.push({
      key: `loose-${item.id}`,
      createdAt: item.createdAt,
      node: (
        <div className="m7r-you">
          <AuthorLine label={authorLabel(item, viewer)} at={item.createdAt} />
          <SentFiles items={[item]} onRetry={onRetryAttachment} />
        </div>
      ),
    });
  }
  const liveTurn = isStreaming ? (
    <StreamingTurn blocks={streamingBlocks} renderOutputs={renderOutputs} />
  ) : null;
  if (liveTurn && streamingAt) {
    entries.push({ key: "streaming-reply", createdAt: streamingAt, node: liveTurn });
  }
  // Stable: a divider stays right under the message it follows.
  entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const rendered: ReactNode[] = [];
  let lastTimed: string | null = null;
  for (const entry of entries) {
    if (!entry.divider) {
      if (startsCluster(lastTimed, entry.createdAt)) {
        rendered.push(<ClusterTime key={`time-${entry.key}`} iso={entry.createdAt} />);
      }
      lastTimed = entry.createdAt;
    }
    rendered.push(<Fragment key={entry.key}>{entry.node}</Fragment>);
  }

  const showEmpty =
    messages.length === 0 &&
    files.byMessage.size === 0 &&
    files.loose.length === 0 &&
    !isStreaming &&
    streamingBlocks.length === 0;

  return (
    <div
      ref={scrollRef}
      className="ws-log"
      role="log"
      aria-live="polite"
      aria-label={ariaLabel}
      aria-busy={isLoading || isStreaming || undefined}
      tabIndex={0}
    >
      <div className="ws-log-col">
        {isLoading ? (
          <div className="m7r-skeleton" aria-label="Loading the thread">
            <i />
            <i />
            <i />
            <i />
          </div>
        ) : showEmpty ? (
          emptyState
        ) : (
          <>
            {olderPages?.hasOlder ? <OlderMessagesRow loading={olderPages.loading} /> : null}
            {rendered}
            {liveTurn && !streamingAt ? liveTurn : null}
            {streamError ? <StreamError message={streamError} /> : null}
          </>
        )}
      </div>
    </div>
  );
}

/** The Quick Create a message was sent with: its pill stays as the record of what was asked for. */
function sentPill(metadata: Record<string, unknown> | null) {
  return <QuickCreatePills items={messageQuickCreates(metadata)} />;
}
